package fr.manyverse.app.domain.usecase

import fr.manyverse.app.domain.image.ImageTarget
import fr.manyverse.app.domain.model.GmInventoryItem
import fr.manyverse.app.domain.model.InventoryItem
import fr.manyverse.app.domain.model.InventoryItemToCreate
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.domain.repository.ImageStorageRepository
import fr.manyverse.app.domain.repository.InventoryRepository
import fr.manyverse.app.domain.util.normalizeForMatching

class SendGmInventoryItemToPlayersUseCase(
    private val inventoryRepository: InventoryRepository,
    private val imageStorageRepository: ImageStorageRepository
) {
    suspend operator fun invoke(
        item: GmInventoryItem,
        targetCharacterIds: List<String>,
        quantity: Int
    ): Result<Unit> {
        for (characterId in targetCharacterIds) {
            // Un consommable déjà possédé (même nom normalisé) est cumulé au lieu d'être dupliqué ;
            // son image existante est conservée
            if (item.category == ItemCategory.CONSUMABLE) {
                val existing = findSameConsumable(characterId, item.name)
                    .onFailure { return Result.failure(it) }.getOrThrow()
                if (existing != null) {
                    inventoryRepository.updateQuantity(existing.id, existing.quantity + quantity)
                        .onFailure { return Result.failure(it) }
                    continue
                }
            }

            val createdItem = inventoryRepository.createItem(
                InventoryItemToCreate(
                    characterId = characterId,
                    name = item.name,
                    category = item.category,
                    quantity = quantity,
                    imageUrl = null
                )
            ).onFailure { return Result.failure(it) }.getOrThrow()

            if (item.imageUrl != null) {
                val imageData = imageStorageRepository.downloadImage(item.imageUrl)
                    .getOrNull() ?: continue
                val newPath = imageStorageRepository
                    .upload(ImageTarget.InventoryItem(characterId, createdItem.id), imageData)
                    .getOrNull() ?: continue
                inventoryRepository.updateImageUrl(createdItem.id, newPath)
            }
        }
        return Result.success(Unit)
    }

    private suspend fun findSameConsumable(characterId: String, name: String): Result<InventoryItem?> {
        val key = name.normalizeForMatching()
        return inventoryRepository.getItemsForCharacter(characterId).map { items ->
            items.firstOrNull { it.category == ItemCategory.CONSUMABLE && it.name.normalizeForMatching() == key }
        }
    }
}
